// this page will contain necessary form fields to create a new contest.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useQuery } from '@tanstack/react-query';

import "../styles/CreateContest.scss"
import Toggle from '../components/Toggle';
import axios from "../scripts/axiosConfig";
import { useAuth } from '../contexts/AuthContext';
import CLink from '../components/CLink';

import { FiInfo, FiPlusCircle, FiTrash2 } from 'react-icons/fi';
import { CiFaceMeh } from "react-icons/ci";
import { RiLoader2Line } from "react-icons/ri";
import { IoIosWarning } from "react-icons/io";
import { z } from 'zod';
import ProblemCard from '../components/ProblemCard';



function FormInput({ label, name, type, required, info, options, toggleChecked, toggleOnChange, dateTimeValue, dateTimeOnChange, inputProps, highlight }) {

    return (

        <div className={"formInput " + type}>
            <div className="text">
                <label htmlFor={name}>
                    {label}
                </label>
                {(info) && (<div className="info">
                    {info}
                </div>)}
            </div>
            <div className="input">
                {(type === 'text') && <input type="text" name={name} id={name} required={required ?? true} placeholder={`Enter ${label}`} {...inputProps} />}
                {(type === 'password') && <input type="password" name={name} id={name} required={required ?? true} placeholder={`Enter ${label}`} {...inputProps} />}
                {(type === 'number') && <input type="number" name={name} id={name} required={required ?? true} {...inputProps} />}
                {(type === 'file') && <input type="file" name={name} id={name} required={required ?? true} />}
                {(type === 'textarea') && <textarea name={name} id={name} required={required ?? true} placeholder={`Enter ${label}`} {...inputProps} />}
                {(type === 'select') && (
                    <select name={name} id={name} onChange={
                        (e) => {
                            options.find(option => option.value === e.target.value).onSelect();
                        }
                    } >
                        {
                            options.map((option, index) => (
                                <option key={index} value={option.value}>{option.label}</option>
                            ))
                        }
                    </select>
                )}
                {(type === 'toggle') && (
                    <Toggle checked={toggleChecked} onChange={toggleOnChange} highlight={highlight} {...inputProps} />
                )}
                {(type === "datetime") && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            value={dateTimeValue}
                            onChange={dateTimeOnChange}
                            sx={{
                                width: "100%",
                                "& .MuiInputBase-input": { height: "58px", borderRadius: ".5rem" },
                                "& .MuiIconButton-root": { padding: "12px", color: "white" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "red" },
                            }}
                        />
                    </LocalizationProvider>
                )}
            </div>

        </div>

    )
}

export default function Create() {

    const { currentUser, USER_PRESENT, userJson } = useAuth();
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const [contestID, setContestID] = useState("");

    // Basic Details
    const [contestName, setContestName] = useState("");
    const [openContestImmediately, setOpenContestImmediately] = useState(true);
    const [openContestForever, setOpenContestForever] = useState(false);
    const [startTime, setStartTime] = useState(dayjs().add(5, 'day'));
    const [endTime, setEndTime] = useState(dayjs().add(6, 'day'));
    const [timedContest, setTimedContest] = useState(false);

    // Problems
    const [randomizeQuestions, setRandomizeQuestions] = useState(false);

    // Security
    const [proctoredMode, setProctoredMode] = useState(false);
    const [passwordProtected, setPasswordProtected] = useState(false);
    const [emailRestriction, setEmailRestriction] = useState("all");
    const [publicContest, setPublicContest] = useState(false);

    const [contestData, setContestData] = useState({
        "name": contestName,
        "openContestImmediately": openContestImmediately,
        "startTime": startTime,
        "openContestForever": openContestForever,
        "endTime": endTime,
        "timedContest": timedContest,
        "duration": null,
        "problems": [],
        "maxQuestions": 0,
        "randomizeQuestions": randomizeQuestions,
        "enableProctored": proctoredMode,
        "maxWarnings": 0,
        "passwordProtected": passwordProtected,
        "password": "",
        "emailRestriction": emailRestriction,
        "allowedEmails": [],
        "emailDomains": [],
        "public": publicContest,
        "orgName": "",
        "customLogo": null,
        "customBanner": null,
        "description": "",
        "eventRules": "",
        "eventPrizes": "",
        "eventFAQ": "",
        "eventTerms": "",
        "eventPrivacy": "",
    });
 
    const formRef = useRef(null);

    const [selectedProblems, setSelectedProblems] = useState([]);

    const handleDragEnd = (result) => {
        if (!result.destination) return;
    
        const newItems = [...selectedProblems];
        const [movedItem] = newItems.splice(result.source.index, 1);
        newItems.splice(result.destination.index, 0, movedItem);
    
        setSelectedProblems(newItems);
    };
    
    useEffect(() => {
        if (currentUser === "none") {
            navigate('/login', { state: { from: '/contests/create' } });
            toast.error("You need to login to create a contest.")
        }
        else {
            console.log(userJson);
        }

    }, [currentUser]);

    useEffect(() => {
        if (emailRestriction === "domain" || emailRestriction === "emails") {
            setPublicContest(false);
        }
    }, [emailRestriction])

    const selectProblem = (id) => {
        if (!selectedProblems.includes(id)) {  // Check if the ID is already selected
            setSelectedProblems(prev => ([...prev, id]));  // Add the actual ID
        }
    };
    
    const deselectProblem = (id) => {
        if (selectedProblems.includes(id)) {  // Check if the ID exists
            setSelectedProblems(prev => prev.filter(problemId => problemId !== id));  // Remove the ID
        }
    };

    const fetchAllProblems = async () => {
        const res = await axios.post('/problem/get-all', { fields: [
            "title", "description", "difficulty", "id", "public", "type"
        ] });
        return res.data;
    };

    const { 
        data: availableProblems, 
        error: availableProblemsError,
        isLoading: availableProblemsLoading, 
        isError: availableProblemsErroring 
    } = useQuery({
        queryKey: ['allProblems'],
        queryFn: fetchAllProblems,
        failureCount: 2,
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnWindowFocus: false,
    });

    const validateContestDataUsingZod = () => {

        return new Promise((resolve, reject) => {
            const contestSchema = z.object({
                name: z.string().min(1, "Contest name is required"),
                openContestImmediately: z.boolean(),
                startTime: z.date().optional(),
                openContestForever: z.boolean(),
                endTime: z.date().optional().nullable(),
                timedContest: z.boolean(),
                duration: z.number().min(1, "Duration must be at least 1 minute").nullable().optional(),
                maxQuestions: z.number().min(1, "There must be at least one question"),
                randomizeQuestions: z.boolean(),
                enableProctored: z.boolean(),
                maxWarnings: z.number().min(0).max(5).optional(),
                passwordProtected: z.boolean(),
                password: z.string().optional(),
                emailRestriction: z.enum(["all", "domain", "emails"]),
                emailDomains: z.array(z.string()).optional(),
                allowedEmails: z.array(z.string()).optional(),
                public: z.boolean(),
                orgName: z.string().optional(),
                customLogo: z.any().optional(),
                customBanner: z.any().optional(),
                description: z.string().min(1, "Event description is required"),
                eventRules: z.string().min(1, "Event rules are required"),
                eventPrizes: z.string().min(1, "Event prizes are required"),
                eventFAQ: z.string().min(1, "Event FAQ is required"),
                eventTerms: z.string().min(1, "Event terms and conditions are required"),
                eventPrivacy: z.string().min(1, "Event privacy policy is required"),
            })
    
            try {
                contestSchema.parse(contestData);
                // toast.success("Validation successful");
                resolve("Validation successful");
            } catch (e) {
                if (e instanceof z.ZodError) {
                    e.errors.forEach(error => {
                        // toast.error(error.message);
                        reject(error.message);
                    });
                }
                reject(e.message);
            }    
        });

    }

    const createContest = async () => {

        toast.info("Validating contest data...", { autoClose: 2000 });

        setTimeout( async () => {
            validateContestDataUsingZod()
            .then((res) => {
                toast.success("Validation successful.", { autoClose: 1000 });
            })
            .catch((err) => {
                toast.error("Validation failed.", { autoClose: 2000 });
                toast.error(err, { autoClose: 5000 });
                return;
            });

            axios.post('/contests/create', { ...contestData }, { headers: { 'Authorization': `Bearer ${await currentUser.getIdToken()}` } })
                .then((res) => {
                    console.log(res);
                    toast.success("Contest created successfully.");
                    if (res.data.redirect) {
                        navigate(res.data.redirect);
                    }
                    else {
                        navigate('/contests');
                    }
                })
                .catch((err) => {
                    console.log(err);
                    toast.error(`Error while creating contest. ${err.message}`);
                });

            console.log(contestData);
        }, 2000);
    }

    return (
        <div className="Create">

            <div className="title">
                <h2>Create new Contest</h2>
                <p>
                    Host your own contest on c0dify. Complete control, proctored mode, and more.
                    Get started by filling the form below.
                </p>
                {
                    (!!contestID) && (
                        <p>
                            Contest ID: {contestID}
                        </p>
                    )
                }
            </div>
            
            <div className="progressContainer">
                <div className="progress"></div>
                <div 
                    className="progress-fill" 
                    style={{
                        "--fill": `${(currentPage - 1) * 30}%`
                    }}
                ></div>
                {
                    new Array(4).fill(0).map((_, index) => (
                        <button
                            className={`
                                progressDot 
                                ${(currentPage > index + 1) && 'completed'} 
                                ${(currentPage === index + 1) && 'active'}`
                            } 
                            key={index}
                            onClick={() => {
                                if ( currentPage > index + 1 ) {
                                    setCurrentPage(index + 1);
                                }
                                else {
                                    toast.error("Click on the next button to proceed.", { autoClose: 2000, toastId: "click_next_to_proceed" });
                                }
                            }}
                        >
                            <span>
                                {index + 1}
                            </span>
                        </button>
                    ))
                }
            </div>
            

            <div className="pages">
                <div className={`formsContainer page-${currentPage}`}>
                    <form 
                    onSubmit={(e) => {
                        e.preventDefault(); 
                        setContestData({
                            ...contestData,
                            name: e.target.contestName.value,
                            openContestImmediately: openContestImmediately,
                            openContestForever: openContestForever,
                            timedContest: timedContest,
                            startTime: openContestImmediately 
                                ? new Date(new Date().setSeconds(0, 0)) 
                                : new Date(startTime.toDate().setSeconds(0, 0)),
                            endTime: openContestForever 
                                ? null 
                                : new Date(endTime.toDate().setSeconds(0, 0)),
                            duration: timedContest ? parseInt(e.target.duration.value) : null,
                        });
                        setCurrentPage(2)
                        console.log("Page 1");
                        console.log(contestData);
                        console.log("--------------------------------------")
                    }} 
                    ref={formRef} 
                    className={`form page1 ${currentPage === 1 ? 'active' : 'hidden'}`}
                >
                        <h2>Basic Details</h2>
                        <FormInput 
                            label={"Contest Name"}
                            name={"contestName"}
                            type={"text"}
                            required={true}
                            info={"Contest's name shown to everyone."}
                        />
                        <FormInput 
                            label={"Open immediately?"}
                            name={"openImmediately"}
                            type={"toggle"}
                            required={false}
                            info={"If checked, contest will be open as soon as you finish all the steps."}
                            toggleChecked={openContestImmediately}
                            toggleOnChange={setOpenContestImmediately}
                        />
                        {
                            (!openContestImmediately) &&
                            <FormInput
                                label={"Start Time"}
                                name={"startTime"}
                                type={"datetime"}
                                required={true}
                                info={"Start time of the contest."}
                                dateTimeValue={startTime}
                                dateTimeOnChange={(value) => setStartTime(value)}
                            />
                        }

                        <FormInput 
                            label={"Open Forever?"}
                            name={"openForever"}
                            type={"toggle"}
                            required={true}
                            info={"If checked, contest will stay open indefinitely until you close it."}
                            toggleChecked={openContestForever}
                            toggleOnChange={setOpenContestForever}

                        />
                        {
                            (!openContestForever) &&
                            <FormInput 
                                label={"End Time"}
                                name={"endTime"}
                                type={"datetime"}
                                info={"After this time, contest will not be open."}
                                required={true}
                                dateTimeValue={endTime}
                                dateTimeOnChange={(value) => setEndTime(value)}
                            />
                        }
                        <FormInput 
                            label={"Timed Contest?"}
                            name={"timedContest"}
                            type={"toggle"}
                            required={true}
                            info={"If unchecked, participants will have unlimited time to complete the contest."}
                            toggleChecked={timedContest}
                            toggleOnChange={setTimedContest}

                        />
                        {
                            (timedContest) &&
                            <>
                                <FormInput 
                                    label={"Duration (in minutes)"}
                                    name="duration"
                                    info={"After the specified duration, participant will be kicked out and marks will be calculated."}
                                    type={"number"}
                                    required={true}
                                />
                            </>
                        }
                        <button type="submit" className="btn btn-primary">Create</button>
                    </form>

                    <form 
                        className={`form page2 ${currentPage === 2 ? 'active' : 'hidden'}`}
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (selectedProblems.length === 0) {
                                toast.error("Please add at least one problem to the contest.");
                                return;
                            }
                            setContestData({
                                ...contestData,
                                problems: selectedProblems,
                                maxQuestions: parseInt(e.target?.maxQuestions?.value),
                                randomizeQuestions: randomizeQuestions,
                            });
                            setCurrentPage(3);
                            console.log("Page 2");
                            console.log(contestData);
                            console.log("--------------------------------------")
                        }}
                    >
                        <h2>Problems</h2>
                        <div className="questionsContainer">
                            <div className="existingQuestions">
                                <h3>
                                    Add Problems from c0dify
                                </h3>
                                <div className="searchbar">
                                    <input type="text" placeholder='search problems to add to contest...' />
                                </div>
                                <div className="toolbar">
                                    <button 
                                        className='create'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.open('/create/problem', '_blank');
                                        }}
                                    >
                                        <FiPlusCircle />
                                        Create New
                                    </button>
                                </div>
                                <div className="problemsList">
                                    {
                                        (availableProblemsLoading) && (
                                            <div className="loading">
                                                <RiLoader2Line />
                                                Contacting HQ for available problems...
                                            </div>
                                        )
                                    }
                                    {
                                        (availableProblemsError) && (
                                            <div className="erroring">
                                                <IoIosWarning size={"30px"} />
                                                Error! Cannot get problems.
                                                {availableProblemsError.toString()}
                                            </div>
                                        )
                                    }
                                    {
                                        (!availableProblemsLoading && availableProblems) && (
                                            availableProblems.map((problem) => {
                                                return (
                                                    <ProblemCard 
                                                        title={problem.title}
                                                        description={problem.description}
                                                        difficulty={problem.difficulty}
                                                        type={problem.type}
                                                        public={problem.public}
                                                        id={problem.id}
                                                        selected={selectedProblems.includes(problem.id)}
                                                        onClick={() => {
                                                            if (selectedProblems.includes(problem.id)) {
                                                                deselectProblem(problem.id);
                                                            }
                                                            else {
                                                                selectProblem(problem.id)
                                                            }}
                                                        }
                                                    />
                                                )
                                            })
                                        )
                                    }
                                </div>
                            </div>

                            <div className="contestQuestions">
                                <h3>
                                    Problems Added to Contest
                                </h3>
                                <div className="searchbar">
                                    <input type="text" placeholder='search added problems...' />
                                </div>
                                <div className="toolbar">
                                    <button 
                                        disabled={selectedProblems.length === 0}
                                        className='delete'
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setSelectedProblems([]);
                                        }}
                                    >
                                        <FiTrash2 />
                                        Remove All
                                    </button>
                                </div>
                                <div className='problemsList'>
                                    {
                                        (selectedProblems.length === 0) && (
                                            <div className="empty">
                                                <CiFaceMeh size='50px' strokeWidth={".01px"} />
                                                You haven't added any problem yet.
                                                Add a problem from the available problems or create your own.
                                            </div>
                                        )
                                    }
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="problemsListID">
                                            {(provided) => (
                                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                                    {selectedProblems.map((problemID, index) => (
                                                        <Draggable key={problemID} draggableId={problemID} index={index}>
                                                            {(provided) => (
                                                                <div
                                                                    className='problem'
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                    }}
                                                                >
                                                                    <div className="details">
                                                                        <CLink className="title" to={"/code/" + problemID} target="_blank">
                                                                            {index + 1}. { availableProblems.find(problem => problem.id === problemID).title }
                                                                        </CLink>
                                                                        <div className="badges">
                                                                            <span className={`difficulty ${availableProblems.find(problem => problem.id === problemID).difficulty}`}>
                                                                                { availableProblems.find(problem => problem.id === problemID).difficulty }
                                                                            </span>
                                                                            <span className={`type ${availableProblems.find(problem => problem.id === problemID).type}`}>
                                                                                { availableProblems.find(problem => problem.id === problemID).type }
                                                                            </span>
                                                                            <span className={`visibility ${availableProblems.find(problem => problem.id === problemID).public ? 'public' : 'private'}`}>
                                                                                {availableProblems.find(problem => problem.id === problemID).public ? "public" : "private"}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <button 
                                                                        onClick={() => deselectProblem(problemID)}
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </div>
                            </div>
                        </div>

                        <FormInput 
                            label="Maximum Questions"
                            name="maxQuestions"
                            required={true}
                            type={"number"}
                            info={`Maximum questions participant has to solve in the contest. Set it to ${selectedProblems.length} to include all problems.`}
                            inputProps={{
                                defaultValue: 0,
                                min: 1,
                                max: Object.keys(selectedProblems)?.length,
                            }}                        
                        />

                        <FormInput 
                            label="Randomize Question Order?"
                            name="randomizeQuestions"
                            type={"toggle"}
                            info={"If unchecked, questions will be shown to participants in the order they are added."}
                            toggleChecked={randomizeQuestions}
                            toggleOnChange={setRandomizeQuestions}
                        />

                        <button type="submit" className="btn btn-primary">Proceed to Security</button>
                    </form>

                    <form 
                        className={`form page3 ${currentPage === 3 ? 'active' : 'hidden'}`}
                        onSubmit={(e) => {
                            e.preventDefault();
                            setContestData({
                                ...contestData,
                                enableProctored: proctoredMode,
                                passwordProtected: passwordProtected,
                                password: e.target.password?.value,
                                emailRestriction: emailRestriction,
                                emailDomains: e.target.emailDomains?.value.split(","),
                                allowedEmails: e.target.allowedEmails?.value.split(","),
                                public: publicContest,
                            });
                            setCurrentPage(4);
                            console.log("Page 3");
                            console.log(contestData);
                            console.log("--------------------------------------")
                        }}
                    >
                        <h2>Security</h2>

                        <FormInput 
                            label={"Enable Proctored Mode?"}
                            name={"enableProctored"}
                            type={"toggle"}
                            required={true}
                            highlight={true}
                            toggleChecked={proctoredMode}
                            toggleOnChange={setProctoredMode}
                            info={"Full screen mode, no copy-paste, no navigation, no right click, no inspect element, no back button, no print, no download, no screenshot, no multiple tabs, no multiple windows, no multiple sessions, no multiple logins, no multiple registrations, no multiple submissions. "}
                        />
                        
                        {
                            (proctoredMode) && (
                                <FormInput 
                                    label={"Maximum Warnings"}
                                    name={"maxWarnings"}
                                    type={"number"}
                                    required={true}
                                    info={"If user gets more than this number of warnings, they will be disqualified. Leave it 0 to allow unlimited warnings. (Max: 5)"}
                                    inputProps={{
                                        min: 0,
                                        max: 5,
                                        autoComplete: "false"
                                    }}
                                />
                            )
                        }

                        <FormInput 
                            label={"Password Protection"}
                            type={"toggle"}
                            info={"Ask users for password before they enter."}
                            toggleChecked={passwordProtected}
                            toggleOnChange={setPasswordProtected}
                        />

                        {
                            (passwordProtected) &&
                            <>
                                <FormInput 
                                    label={"Password"}
                                    name="password"
                                    type={"password"}
                                    required={true}
                                />
                            </>
                        }

                        <FormInput 
                            label={"Email Restrictions?"}
                            name={"emailRestrictions"}
                            info={"Choose if special email restrictions are needed."}
                            type={"select"}
                            options={[
                                {value: "all", label: "All Emails", onSelect: () => setEmailRestriction("all")},
                                {value: "domain", label: "Only emails with specific domain", onSelect: () => setEmailRestriction("domain")},
                                {value: "emails", label: "Only specific Emails", onSelect: () => setEmailRestriction("emails") },
                            ]}
                        />

                        {
                            (emailRestriction === "domain") && (
                                <FormInput 
                                    label={"Allowed Email Domains (without the @ sign)"}
                                    name={"emailDomains"}
                                    type={"text"}
                                    info={"Enter comma separated list of allowed email domains."}
                                    required={true}
                                />
                            )
                        }

                        {
                            (emailRestriction === "emails") && (
                                <FormInput 
                                    label={"Allowed Emails"}
                                    name={"allowedEmails"}
                                    type={"textarea"}
                                    info={"Enter comma separated list of allowed emails."}
                                    required={true}
                                />
                            )
                        }

                        <FormInput 
                            label={"Public Contest?"}
                            name={"public"}
                            info={"If checked, contest will be featured. If unchecked, only users with the link can participate."}
                            type={"toggle"}
                            highlight={true}
                            inputProps={{
                                disabled: emailRestriction !== "all"
                            }}
                            toggleChecked={ publicContest }
                            toggleOnChange={(value) => {
                                console.log(`Public: ${value}`);
                                setPublicContest(value);
                            }} 
                        />
                        
                        {
                            (proctoredMode) && (
                                <div>
                                    <small>
                                        <FiInfo />
                                        Users who try to cheat will be disqualified as per the number of warning setting. 
                                        You will be able to view, block, and unblock users in the contest dashboard.
                                    </small>
                                </div>
                            )
                        }

                        <button type="submit" className="btn btn-primary">Proceed to Customization</button>
                    </form>

                    <form 
                        className={`form page4 ${currentPage === 4 ? 'active' : 'hidden'}`}
                        onSubmit={(e) => {
                            e.preventDefault();
                            console.log(`Public Status: ${publicContest}`);
                            setContestData({
                                ...contestData,
                                orgName: e.target.orgName.value,
                                customLogo: e.target.customLogo?.files ? e.target.customLogo.files[0] : null,
                                customBanner: e.target.customBanner?.files ? e.target.customBanner.files[0] : null,
                                description: e.target.description.value,
                                eventRules: e.target.eventRules.value,
                                eventPrizes: e.target.eventPrizes.value,
                                eventFAQ: e.target.eventFAQ.value,
                                eventTerms: e.target.eventTerms.value,
                                eventPrivacy: e.target.eventPrivacy.value,
                            });

                            createContest();
                        }}
                    >
                        <h2 className='gradientText'>Customization</h2>
                        <FormInput 
                            label={"Organization Name"}
                            name={"orgName"}
                            type={"text"}
                            info={"Users will see your Organization name instead of your personal name."}
                            required={false}
                        />

                        <FormInput 
                            label={"Custom Logo"}
                            name={"customLogo"}
                            type={"file"}
                            info={"Upload only square .ico or .png transparent logos (size: 100x100px)."}
                            required={false}
                        />

                        <FormInput 
                            label={"Custom Banner"}
                            name={"customLogo"}
                            type={"file"}
                            info={"Upload rectangle banner images (size: 700x300px)."}
                            required={false}
                        />

                        <FormInput 
                            label={"Event Description"}
                            name={"description"}
                            type={"textarea"}
                            // info={"Upload only square .ico or .png transparent logos (size: 100x100px)."}
                            required={true}
                        />

                        <FormInput 
                            label={"Event Rules"}
                            name={"eventRules"}
                            type={"textarea"}
                            required={true}
                            inputProps={{
                                defaultValue: "Participants must register before the contest starts.\nEach participant must write original code. Plagiarism will result in disqualification. \nParticipants can use any programming language unless otherwise specified. \nThe contest duration is fixed, and submissions after the deadline won't be accepted. \nDecisions made by the contest organizers/judges are final."
                            }}
                        />
                        
                        <FormInput 
                            label={"Event Prizes"}
                            name={"eventPrizes"}
                            type={"textarea"}
                            required={true}
                        />
                        
                        <FormInput 
                            label={"Event FAQ"}
                            name={"eventFAQ"}
                            type={"textarea"}
                            required={true}
                        />

                        <FormInput 
                            label={"Event Terms and Conditions"}
                            name={"eventTerms"}
                            type={"textarea"}
                            required={true}
                            inputProps={{
                                defaultValue: "By participating, you agree to abide by all contest rules and regulations. \nThe organizers reserve the right to modify or cancel the contest at any time. \nAny attempt to exploit the platform or manipulate results will lead to disqualification. \nParticipants must provide valid contact details for prize distribution. \nThe contest organizers are not responsible for any technical issues faced during participation."
                            }}
                        />
                        
                        <FormInput 
                            label={"Event Privacy Policy"}
                            name={"eventPrivacy"}
                            type={"textarea"}
                            required={true}
                            inputProps={{
                                defaultValue: "We collect participant details (name, email, and coding submissions) solely for contest purposes. \nYour data will not be shared with third parties without your consent. \nContest submissions may be used for promotional or educational purposes, with proper credit. \nYou may request the deletion of your data after the contest concludes. \nBy participating, you agree to our privacy terms."
                            }}
                        />

                        <div className="buttons">
                            <button 
                                onClick={(e) => {
                                    e.preventDefault();
                                    let data = JSON.stringify({
                                        startTime: new Date(startTime),
                                        endTime: new Date(endTime),
                                        name: contestData.name,
                                        description: contestData.description,
                                        eventRules: contestData.eventRules,
                                        eventPrizes: contestData.eventPrizes,
                                        eventTerms: contestData.eventTerms,
                                        eventPrivacy: contestData.eventPrivacy,
                                        eventFAQ: contestData.eventFAQ,
                                        duration: contestData.duration,
                                        enableProctored: contestData.enableProctored,
                                        userHasRegistered: false,
                                        openContestForever: contestData.openContestForever,
                                        creatorName: "Test Creator",
                                        tags: [],
                                    });
                                    console.log(contestData)
                                    window.open(`/contests/preview?data=${encodeURIComponent(data)}`, "_blank");
                                }}
                                className='btn-primary'
                            >Preview</button>
                            <button 
                                type="submit" 
                                className="btn"
                            >
                                Create
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    )
}